// Wraps the Screen Wake Lock API with reacquire-on-visibility-change behavior.
// Falls back gracefully (reports unavailable) on browsers that don't support it —
// this is a real limitation of some Android WebViews / older Chrome versions and
// is surfaced to the user rather than silently faked.

export type WakeLockStatus = 'unsupported' | 'active' | 'released' | 'error';

export class WakeLockManager {
  private sentinel: WakeLockSentinel | null = null;
  private wantActive = false;
  private listeners = new Set<(status: WakeLockStatus) => void>();
  private visibilityHandler = () => {
    if (this.wantActive && document.visibilityState === 'visible' && !this.sentinel) {
      void this.acquire();
    }
  };

  constructor() {
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  get isSupported(): boolean {
    return 'wakeLock' in navigator;
  }

  subscribe(listener: (status: WakeLockStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(status: WakeLockStatus): void {
    for (const listener of this.listeners) listener(status);
  }

  async acquire(): Promise<void> {
    this.wantActive = true;
    if (!this.isSupported) {
      this.emit('unsupported');
      return;
    }
    // navigator.wakeLock.request() is spec-required to REJECT while the
    // document isn't visible (both Chrome and Firefox enforce this). Without
    // this guard, a hidden tab could get stuck in a runaway retry loop: the
    // browser auto-releases the wake lock the instant the tab hides, which
    // fires the sentinel's own 'release' listener below -> emit('released')
    // -> every subscriber (see performance.ts's handleWakeLockStatus) calls
    // acquire() again -> request() rejects immediately (still hidden) ->
    // emit('error') -> the same subscriber calls acquire() again -> repeat,
    // for as long as the tab stays hidden. Each iteration is only a
    // microtask apart (a rejected promise settles almost immediately), so
    // this can spin hundreds/thousands of times while backgrounded and is
    // often still unwinding by the time the tab is foregrounded again —
    // exactly the "freezes when I switch back" symptom this was causing,
    // with no audio/playback involved at all (this runs the instant the
    // Player screen mounts). visibilityHandler below already re-acquires
    // the moment the tab genuinely becomes visible, so simply not attempting
    // while hidden loses nothing — wantActive still remembers the intent.
    if (document.visibilityState !== 'visible') return;
    try {
      this.sentinel = await navigator.wakeLock.request('screen');
      this.emit('active');
      this.sentinel.addEventListener('release', () => {
        this.sentinel = null;
        if (this.wantActive && document.visibilityState === 'visible') this.emit('released');
      });
    } catch {
      if (document.visibilityState === 'visible') this.emit('error');
    }
  }

  async release(): Promise<void> {
    this.wantActive = false;
    if (this.sentinel) {
      await this.sentinel.release();
      this.sentinel = null;
    }
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }
}

export const wakeLockManager = new WakeLockManager();
